import { useId, useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import StoryThreeBackground from './StoryThreeBackground';
import './OurStorySection.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export default function OurStorySection({ ready = true, storyHref = '/our-story' }) {
  const scope = useRef(null);
  const stage = useRef(null);
  const titleId = useId();

  useGSAP(() => {
    if (!ready) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(scope);
      const scroll = { trigger: scope.current, start: 'top bottom', end: 'bottom top', scrub: 1 };
      const floating = gsap.to(select('.our-story__logo-float'), {
        y: -4, duration: 4.5, delay: 1.85, repeat: -1, yoyo: true, ease: 'sine.inOut',
        scrollTrigger: {
          trigger: scope.current, start: 'top 80%', end: 'bottom top',
          toggleActions: 'play pause resume pause',
        },
      });
      const leafFloating = gsap.to(select('.our-story__leaf-float'), {
        y: -6, rotation: 3, transformOrigin: '50% 100%',
        duration: 3.5, delay: 1.25, repeat: -1, yoyo: true, ease: 'sine.inOut',
        scrollTrigger: {
          trigger: scope.current, start: 'top 80%', end: 'bottom top',
          toggleActions: 'play pause resume pause',
        },
      });
      const onVisibility = () => {
        const bounds = scope.current.getBoundingClientRect();
        const visible = !document.hidden && bounds.bottom > 0 && bounds.top < window.innerHeight * 0.8;
        [floating, leafFloating].forEach((animation) => {
          if (visible) animation.resume();
          else animation.pause();
        });
      };
      document.addEventListener('visibilitychange', onVisibility);

      const entrance = gsap.timeline({
        defaults: { duration: 0.8, ease: 'power3.out' },
        scrollTrigger: { trigger: scope.current, start: 'top 80%', once: true },
      });
      ['eyebrow', 'title', 'description', 'cta'].forEach((name, index) => {
        entrance.from(select(`.our-story__${name}`), { autoAlpha: 0, y: [12, 35, 25, 20][index] }, index * 0.12);
      });
      entrance
        .from(select('.our-story__card-entrance'), { autoAlpha: 0, x: 60, scale: 0.95, rotation: 1.5, duration: 1.1 }, 0.1)
        .from(select('.our-story__leaf-image'), { autoAlpha: 0, scale: 0.65, rotation: -12, transformOrigin: '50% 100%', duration: 1 }, 0.25)
        .from(select('.our-story__logo'), {
          autoAlpha: 0, scale: 0.9, duration: 0.8,
        }, 1.05);

      gsap.fromTo(select('.our-story__card-scroll'), { y: 15 }, { y: -15, ease: 'none', scrollTrigger: { ...scroll } });
      gsap.fromTo(select('.our-story__logo-scroll'), { y: 8 }, { y: -8, ease: 'none', scrollTrigger: { ...scroll } });
      gsap.to(select('.our-story__leaf'), { y: -8, x: 3, rotation: 1, transformOrigin: '50% 50%', ease: 'none', scrollTrigger: { ...scroll } });
      return () => document.removeEventListener('visibilitychange', onVisibility);
    });

    media.add('(min-width: 768px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)', () => {
      const area = stage.current;
      const card = area.querySelector('.our-story__card');
      const options = { duration: 0.7, ease: 'power3.out' };
      const rotateX = gsap.quickTo(card, 'rotationX', options);
      const rotateY = gsap.quickTo(card, 'rotationY', options);
      const translateY = gsap.quickTo(card, 'y', options);
      const move = (event) => {
        const rect = area.getBoundingClientRect();
        const x = gsap.utils.clamp(-1, 1, (event.clientX - rect.left) / rect.width * 2 - 1);
        const y = gsap.utils.clamp(-1, 1, (event.clientY - rect.top) / rect.height * 2 - 1);
        rotateX(-y * 2);
        rotateY(x * 2.5);
        translateY(y * 2);
      };
      const leave = () => { rotateX(0); rotateY(0); translateY(0); };
      area.addEventListener('pointermove', move);
      area.addEventListener('pointerleave', leave);
      return () => {
        area.removeEventListener('pointermove', move);
        area.removeEventListener('pointerleave', leave);
      };
    });
    return () => media.revert();
  }, { scope, dependencies: [ready], revertOnUpdate: true });

  return (
    <section ref={scope} className="our-story" aria-labelledby={titleId}>
      <div className="our-story__inner">
        <div className="our-story__content">
          <p className="our-story__eyebrow">OUR STORY</p>
          <h2 className="our-story__title" id={titleId}>Rooted in Jamaica.<br />Made for connection.</h2>
          <p className="our-story__description">Founded by Donna Forbes, JamRock Escape offers a thoughtfully prepared home away from home. Every stay begins with a simple promise: warmth, comfort, and a welcome that makes you feel cared for.</p>
          <a className="our-story__cta" href={storyHref}>Get to know us <span aria-hidden="true">↗</span></a>
          <div className="our-story__leaf" aria-hidden="true">
            <div className="our-story__leaf-float">
              <img className="our-story__leaf-image" src={`${process.env.PUBLIC_URL}/images/hummingbird.svg`} alt="" width="180" height="140" />
            </div>
          </div>
        </div>
        <div ref={stage} className="our-story__stage">
          <StoryThreeBackground stageRef={stage} ready={ready} />
          <div className="our-story__card-scroll">
            <div className="our-story__card-entrance">
              <div className="our-story__card">
                <div className="our-story__logo-scroll">
                  <div className="our-story__logo-float">
                    <img className="our-story__logo" src={`${process.env.PUBLIC_URL}/images/logo.jpg`} alt="JamRock Escape" width="1536" height="1024" />
                  </div>
                </div>
                <div className="our-story__divider" />
                <p className="our-story__tagline">a yah suh nice</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
